/**
 * BusinessIntakePage — `/partner/business-intake`
 *
 * Phase 1c of the PooPartner Intake Revision. Auth-gated 5-step
 * business-only wizard. Writes to `/api/poopartner/intake` (multipart)
 * on Step-5 submit.
 *
 * Secrets discipline:
 *   * EIN + W-9 held in React `useState` only. NO localStorage /
 *     sessionStorage — a full-page reload clears state and forces
 *     re-entry, which is the correct posture for PII / uploaded
 *     documents.
 *   * Turnstile: reads `REACT_APP_TURNSTILE_SITE_KEY`. Empty /
 *     placeholder → renders the "verification will be enabled once
 *     Cloudflare Turnstile is provisioned" info box. Backend accepts
 *     `captcha_status=skipped_not_configured` in this mode; submit
 *     stays enabled.
 *   * On network error / server 400: field-level errors surface
 *     inline via toast + step-anchor.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { API_BASE, api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck, UploadCloud, ExternalLink } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import PublicPageShell from "@/components/PublicPageShell";
import FieldError from "@/components/a11y/FieldError";
import PoliteStatus from "@/components/a11y/PoliteStatus";
import {
  POOPARTNER_REVENUE_BANDS,
  POOPARTNER_ONLINE_PRESENCE_FIELDS,
  POOPARTNER_INTAKE_LEGAL_TEXT,
  POOPARTNER_LEGAL_ROUTES,
  POOPARTNER_ENTITY_TYPES,
  POOPARTNER_W9_ALLOWED_MIMES,
  POOPARTNER_W9_MAX_BYTES,
  POOPARTNER_W9_IRS_URL,
} from "@/lib/poopartnerConstants";

const STEPS = [
  { idx: 0, label: "Business identity" },
  { idx: 1, label: "Business address" },
  { idx: 2, label: "EIN + W-9" },
  { idx: 3, label: "Revenue + online presence" },
  { idx: 4, label: "Review + submit" },
];

// Client-side email regex — cheap sanity gate; server does the
// authoritative validation.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// US phone: 10 digits, optional +1 / punctuation. Match is permissive
// (server normalizes).
const PHONE_RE = /^\+?1?[\s\-\.]?\(?\d{3}\)?[\s\-\.]?\d{3}[\s\-\.]?\d{4}$/;

// EIN input mask: user types digits, we render `XX-XXXXXXX`. Store
// digits-only in state and format on render.
function formatEIN(digitsOnly) {
  const s = (digitsOnly || "").replace(/\D/g, "").slice(0, 9);
  if (s.length <= 2) return s;
  return `${s.slice(0, 2)}-${s.slice(2)}`;
}

function isTurnstileConfigured() {
  const k = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  return k && k !== "PLACEHOLDER_UNTIL_PROVISIONED";
}

export default function BusinessIntakePage() {
  const navigate = useNavigate();

  // Wizard step index
  const [step, setStep] = useState(0);

  // Step 1 — identity
  const [businessName, setBusinessName] = useState("");
  const [entityType, setEntityType] = useState("");
  const [dba, setDba] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  // Step 2 — address (Smarty-verified via AddressAutocomplete)
  const [address, setAddress] = useState({
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    address_verified: false,
  });

  // Step 3 — EIN + W-9
  const [einDigits, setEinDigits] = useState("");
  const [w9File, setW9File] = useState(null);
  const [w9Error, setW9Error] = useState("");

  // Step 4 — revenue band + online presence
  const [revenueBand, setRevenueBand] = useState("");
  const [online, setOnline] = useState({});

  // Step 5 — legal + captcha + submit
  const [legalChecked, setLegalChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const turnstileConfigured = isTurnstileConfigured();

  // Step 1 validity gate
  const step1Valid = useMemo(
    () =>
      businessName.trim().length > 0 &&
      businessName.length <= 120 &&
      POOPARTNER_ENTITY_TYPES.some((e) => e.id === entityType) &&
      (dba || "").length <= 120 &&
      PHONE_RE.test(phone) &&
      EMAIL_RE.test(email),
    [businessName, entityType, dba, phone, email],
  );

  const step2Valid = Boolean(address.address_verified);
  const step3Valid = einDigits.length === 9 && !!w9File && !w9Error;
  const step4Valid = POOPARTNER_REVENUE_BANDS.some((b) => b.id === revenueBand);
  const step5Valid =
    legalChecked && (turnstileConfigured ? captchaToken.length > 0 : true);

  const canProceed = [step1Valid, step2Valid, step3Valid, step4Valid, step5Valid][step];

  const handleW9Change = (file) => {
    setW9Error("");
    setW9File(null);
    if (!file) return;
    if (!POOPARTNER_W9_ALLOWED_MIMES.includes(file.type)) {
      setW9Error("W-9 must be PDF, JPG, or PNG.");
      return;
    }
    if (file.size > POOPARTNER_W9_MAX_BYTES) {
      setW9Error("W-9 must be ≤10 MB.");
      return;
    }
    setW9File(file);
  };

  const submit = async () => {
    if (!step5Valid || submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("business_name", businessName.trim());
      fd.append("entity_type", entityType);
      if (dba.trim()) fd.append("dba", dba.trim());
      fd.append("business_phone", phone.trim());
      fd.append("business_email", email.trim());
      fd.append("address_line1", address.address_line1);
      if (address.address_line2) fd.append("address_line2", address.address_line2);
      fd.append("address_city", address.city);
      fd.append("address_state", address.state);
      fd.append("address_zip", address.zip);
      fd.append("ein", einDigits);
      fd.append("revenue_band", revenueBand);
      // Filter out empty values so backend URL validator doesn't
      // trip on ""; keep only fields the user actually filled in.
      const onlineClean = {};
      Object.entries(online).forEach(([k, v]) => {
        if (v && v.trim()) onlineClean[k] = v.trim();
      });
      fd.append("online_presence", JSON.stringify(onlineClean));
      fd.append("legal_accepted", "true");
      fd.append(
        "captcha_token",
        turnstileConfigured ? captchaToken : "placeholder-mode-token",
      );
      fd.append("w9_file", w9File, w9File.name);

      const token = localStorage.getItem("pp_token");
      const resp = await fetch(`${API_BASE}/poopartner/intake`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const err = body?.detail?.message || body?.detail?.error || `HTTP ${resp.status}`;
        toast.error(String(err));
        setSubmitting(false);
        return;
      }
      toast.success("Application submitted for review.");
      navigate("/partner/intake/status");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.message || String(e)) || "Submit failed.");
      setSubmitting(false);
    }
  };

  return (
    <PublicPageShell mainClass="flex justify-center px-4 py-10">
      <div
        className="w-full max-w-3xl bg-white border border-pp-border rounded-lg shadow-sm p-6 md:p-10"
        data-testid="business-intake-page"
      >
        <header className="mb-6">
          <h1
            className="text-2xl md:text-3xl font-semibold text-pp-text"
            data-testid="business-intake-title"
          >
            Become a verified PooPartner
          </h1>
          <p className="text-sm text-pp-text-muted mt-1">
            Step {step + 1} of {STEPS.length} — {STEPS[step].label}
          </p>
          <ol
            className="flex gap-1 mt-4"
            data-testid="business-intake-stepper"
            aria-label={`Application steps: step ${step + 1} of ${STEPS.length}`}
          >
            {STEPS.map((s) => {
              const state =
                s.idx < step
                  ? "complete"
                  : s.idx === step
                  ? "current"
                  : "upcoming";
              return (
                <li
                  key={s.idx}
                  className={
                    "flex-1 h-1.5 rounded " +
                    (s.idx <= step ? "bg-pp-orange" : "bg-pp-surface-2")
                  }
                  data-testid={`business-intake-stepper-tick-${s.idx}`}
                  aria-label={`Step ${s.idx + 1} of ${STEPS.length}, ${s.label}, ${state}`}
                  {...(s.idx === step ? { "aria-current": "step" } : {})}
                />
              );
            })}
          </ol>
          {/* Phase L.2 P2 — audit BI-6. Polite region announces
              step transitions ("Step 3 of 5, EIN + W-9") to SR
              users when the user clicks Next / Back. Region stays
              mounted for the transition to fire correctly. */}
          <PoliteStatus
            visuallyHidden
            message={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step].label}.`}
            testId="business-intake-step-status"
          />
        </header>

        {step === 0 && (
          <section className="space-y-4" data-testid="business-intake-step-1">
            <div>
              <Label htmlFor="bn">Business name</Label>
              <Input
                id="bn"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                maxLength={120}
                required
                data-testid="business-intake-business-name-input"
              />
            </div>
            <div>
              <Label htmlFor="business-intake-entity-type-select">
                Legal entity type
              </Label>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger
                  id="business-intake-entity-type-select"
                  data-testid="business-intake-entity-type-select"
                >
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {POOPARTNER_ENTITY_TYPES.map((et) => (
                    <SelectItem
                      key={et.id}
                      value={et.id}
                      data-testid={`business-intake-entity-type-option-${et.id}`}
                    >
                      {et.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="dba">DBA / trade name (optional)</Label>
              <Input
                id="dba"
                value={dba}
                onChange={(e) => setDba(e.target.value)}
                maxLength={120}
                data-testid="business-intake-dba-input"
              />
            </div>
            <div>
              <Label htmlFor="ph">Business phone</Label>
              <Input
                id="ph"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                data-testid="business-intake-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="em">Business email</Label>
              <Input
                id="em"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="business-intake-email-input"
              />
            </div>
          </section>
        )}

        {step === 1 && (
          <section className="space-y-4" data-testid="business-intake-step-2">
            <p className="text-sm text-pp-text-muted">
              Enter the address where your business receives mail. We verify against USPS
              records via Smarty.
            </p>
            <AddressAutocomplete
              value={address}
              onChange={(next) => setAddress({ ...address, ...next })}
            />
            {address.address_verified && (
              <p
                className="text-sm text-pp-orange"
                data-testid="business-intake-address-verified-hint"
              >
                <ShieldCheck className="inline-block mr-1" size={14} /> Address verified.
              </p>
            )}
          </section>
        )}

        {step === 2 && (
          <section className="space-y-4" data-testid="business-intake-step-3">
            <div>
              <Label htmlFor="ein">EIN (Employer Identification Number)</Label>
              <Input
                id="ein"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={formatEIN(einDigits)}
                onChange={(e) => setEinDigits(e.target.value.replace(/\D/g, "").slice(0, 9))}
                placeholder="XX-XXXXXXX"
                data-testid="business-intake-ein-input"
              />
              <p className="text-xs text-pp-text-muted mt-1">
                We use your EIN to verify your business with the IRS. It is encrypted at rest
                and only PooPluckers admins can view it during your application review.
              </p>
            </div>
            <div>
              <Label htmlFor="w9">Upload your completed W-9</Label>
              <Input
                id="w9"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => handleW9Change(e.target.files?.[0] || null)}
                data-testid="business-intake-w9-input"
                aria-invalid={Boolean(w9Error) || undefined}
                aria-describedby={w9Error ? "business-intake-w9-error" : undefined}
              />
              {w9File && (
                <p
                  className="text-xs text-pp-text-muted mt-1"
                  data-testid="business-intake-w9-selected-hint"
                >
                  <UploadCloud className="inline-block mr-1" size={14} />
                  {w9File.name} ({Math.round(w9File.size / 1024)} KB)
                </p>
              )}
              {w9Error && (
                <FieldError
                  id="business-intake-w9-error"
                  message={w9Error}
                  testId="business-intake-w9-error"
                />
              )}
              <p className="text-xs text-pp-text-muted mt-2">
                Need the form?{" "}
                <a
                  href={POOPARTNER_W9_IRS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  data-testid="business-intake-w9-irs-link"
                >
                  Download IRS Form W-9 <ExternalLink className="inline-block" size={12} aria-hidden="true" />
                  <span className="sr-only"> (opens in new tab)</span>
                </a>
              </p>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="space-y-6" data-testid="business-intake-step-4">
            {/* Phase L.2 P1 — audit BI-2. `<p>` heading paired with a
                RadioGroup via `aria-labelledby` so screen-reader users
                hear the group's accessible name ("Annual revenue")
                before radiogroup entry. */}
            <div>
              <p
                id="business-intake-revenue-band-legend"
                className="font-medium text-pp-text mb-2"
              >
                Annual revenue
              </p>
              <RadioGroup
                value={revenueBand}
                onValueChange={setRevenueBand}
                aria-labelledby="business-intake-revenue-band-legend"
                data-testid="business-intake-revenue-band-radiogroup"
              >
                {POOPARTNER_REVENUE_BANDS.map((b) => (
                  <div key={b.id} className="flex items-center gap-2">
                    <RadioGroupItem
                      value={b.id}
                      id={`rb-${b.id}`}
                      data-testid={`business-intake-revenue-band-radio-${b.id}`}
                    />
                    <Label htmlFor={`rb-${b.id}`}>{b.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Phase L.2 P1 — audit BI-3. URL-input grid wrapped in a
                `role="group" aria-labelledby=…` so the "Your online
                presence" heading is programmatically related to the
                fields it governs. */}
            <div
              role="group"
              aria-labelledby="business-intake-online-presence-heading"
            >
              <p
                id="business-intake-online-presence-heading"
                className="font-medium text-pp-text"
                data-testid="business-intake-online-presence-heading"
              >
                Your online presence
              </p>
              <p
                className="text-xs text-pp-text-muted mb-3"
                data-testid="business-intake-online-presence-subhead"
              >
                all optional — share what you have
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {POOPARTNER_ONLINE_PRESENCE_FIELDS.map((f) => (
                  <div key={f.id}>
                    <Label htmlFor={`op-${f.id}`}>{f.label}</Label>
                    <Input
                      id={`op-${f.id}`}
                      type="url"
                      value={online[f.id] || ""}
                      onChange={(e) =>
                        setOnline({ ...online, [f.id]: e.target.value })
                      }
                      placeholder={f.help_text || "https://"}
                      data-testid={`business-intake-online-presence-input-${f.id}`}
                    />
                    {f.help_text && (
                      <p className="text-xs text-pp-text-muted mt-1">{f.help_text}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {step === 4 && (
          <section className="space-y-4" data-testid="business-intake-step-5">
            <div className="rounded border border-pp-border bg-pp-surface-2 p-3">
              <p className="text-sm text-pp-text" data-testid="business-intake-legal-text">
                {POOPARTNER_INTAKE_LEGAL_TEXT}
              </p>
              <ul className="text-xs mt-2 space-y-1">
                <li>
                  <a
                    href={POOPARTNER_LEGAL_ROUTES.terms}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-pp-orange"
                    data-testid="business-intake-legal-link-terms"
                  >
                    Terms of Service
                    <span className="sr-only"> (opens in new tab)</span>
                  </a>
                </li>
                <li>
                  <a
                    href={POOPARTNER_LEGAL_ROUTES.partnerAgreement}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-pp-orange"
                    data-testid="business-intake-legal-link-partner-agreement"
                  >
                    PooPartner Agreement
                    <span className="sr-only"> (opens in new tab)</span>
                  </a>
                </li>
                <li>
                  <a
                    href={POOPARTNER_LEGAL_ROUTES.privacy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-pp-orange"
                    data-testid="business-intake-legal-link-privacy"
                  >
                    Privacy Policy
                    <span className="sr-only"> (opens in new tab)</span>
                  </a>
                </li>
              </ul>
              <div className="flex items-center gap-2 mt-3">
                <Checkbox
                  id="legal-check"
                  checked={legalChecked}
                  onCheckedChange={(v) => setLegalChecked(!!v)}
                  data-testid="business-intake-legal-checkbox"
                />
                <Label htmlFor="legal-check">I agree</Label>
              </div>
            </div>

            <div
              className="rounded border border-pp-border p-3"
              data-testid="business-intake-captcha-box"
            >
              {turnstileConfigured ? (
                <p className="text-sm text-pp-text-muted">
                  Cloudflare Turnstile widget renders here (configured).
                </p>
              ) : (
                <p
                  className="text-sm text-pp-text-muted"
                  data-testid="business-intake-captcha-placeholder-copy"
                >
                  Bot-check will be enabled once Cloudflare Turnstile is provisioned.
                  Your submission will be reviewed the same way in either case.
                </p>
              )}
            </div>
          </section>
        )}

        <nav className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || submitting}
            data-testid="business-intake-back-btn"
          >
            Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={!canProceed}
              data-testid="business-intake-next-btn"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={!canProceed || submitting}
              data-testid="business-intake-submit-btn"
              aria-busy={submitting || undefined}
            >
              {submitting && (
                <Loader2 className="animate-spin mr-2 inline-block" size={16} aria-hidden="true" />
              )}
              Submit application
            </Button>
          )}
        </nav>
        {/* Phase L.2 P2 — audit BI-8. Polite region announces the
            submit-in-progress state so SR users get feedback while
            the multi-second multipart upload is in flight. */}
        <PoliteStatus
          visuallyHidden
          message={submitting ? "Submitting your application…" : ""}
          testId="business-intake-submit-status"
        />
      </div>
    </PublicPageShell>
  );
}
